import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NestedField } from 'react-form';
import moment from 'moment-timezone';

import { required } from '../../../../utils/form-validation';
import {
    TeamTennisClubSearch,
    Select,
    Input,
    CheckBox,
    DateTimePicker,
} from '../../../Form';

import { getApiAxiosInstance } from '../../../../utils/network';
import { getTimeZones } from '../../../../utils/events';
import { getAllClubLocations } from '../../../../api/clubLocationsApi';
import SelectIsConference from './SelectIsConference';

const AdultTeamTennisMatch = (props) => {
    const {
        defaultValues,
        formApi,
        clubDetails,
        lastEventIds,
        showSelectIsStandings,
    } = props;
    const { values } = formApi;

    const coreUITimeZones = useSelector((state) => state.coreUI.types.timeZones);
    const [teamMatchSelectedOpponent, setTeamMatchSelectedOpponent] = useState(null);
    const [teamMatchLocationOptions, setTeamMatchLocationOptions] = useState([]);
    const [teamMatchEventScheduleStartDate, setTeamMatchEventScheduleStartDate] = useState(null);
    const [timezone, setTimezone] = useState(null);
    const [showTimezone, setShowTimezone] = useState(false);
    const [clubLocations, setClubLocations] = useState([]);

    useEffect(() => {
        const timeZone = defaultValues?.eventSchedule?.timeZone || 77;
        const timezoneObj = coreUITimeZones.find(tz => { return tz.id === parseInt(timeZone); });
        setTeamMatchEventScheduleStartDate(defaultValues.eventSchedule ? moment(defaultValues.eventSchedule.eventStartUtc).tz(timezoneObj.value) : null);
        setTimezone(timeZone);
        // set the conference value to true
        formApi.setValue('$isConference', '2');

        (async () => {
            const { data } = await getAllClubLocations(getApiAxiosInstance(), values.club);
            setClubLocations(data.clubLocations);
        })();
    }, []);

    const handleClubClearClick = () => {
        formApi.setValue('name', null);
        formApi.setValue('dualMatchClubs', null);
        formApi.setValue('clubLocationId', null);
        formApi.setValue('eventLocations', defaultValues.eventLocations);

        setTeamMatchSelectedOpponent(null);
    };

    const setLocationOptions = async (opponentClub) => {
        const { data } = await getAllClubLocations(getApiAxiosInstance(), opponentClub.id);

        const locationOptions = [];
        function createLocationOption(location, locationClubId, opponentClubLocation) {
            return {
                id: location.id,
                clubId: locationClubId,
                value: location.location.googleFormattedName,
                label: `${location.name} - ${opponentClubLocation.name}`,
            };
        }

        // current club locations
        clubLocations.forEach(l => {
            locationOptions.push(createLocationOption(l, values.club, clubDetails));
        });
        // opponent club locations
        data.clubLocations.forEach(l => {
            locationOptions.push(createLocationOption(l, opponentClub.id, opponentClub));
        });

        setTeamMatchLocationOptions(locationOptions);
    };

    const handleTeamTennisClubSelect = (opponentClub, currentClubIsHome, opponentClubIsHome) => {
        const currentClubName = clubDetails.name;
        const opponentClubName = opponentClub.name;
        const dualMatchClubs = [{
            clubId: values.club,
            isHome: currentClubIsHome,
        }, {
            clubId: opponentClub.id,
            isHome: opponentClubIsHome,
        }];

        formApi.setValue('dualMatchClubs', dualMatchClubs);

        if (currentClubIsHome) {
            formApi.setValue('name', `${opponentClubName} at ${currentClubName} `);
        } else if (opponentClubIsHome) {
            formApi.setValue('name', `${currentClubName} at ${opponentClubName} `);
        } else {
            formApi.setValue('name', `${opponentClubName} vs. ${currentClubName} `);
        }

        setTeamMatchSelectedOpponent(opponentClub);
        setLocationOptions(opponentClub);
    };

    const toggleTimezone = (show, val) => {
        const date = teamMatchEventScheduleStartDate;
        if (val && formApi) {
            const timezoneObj = coreUITimeZones.find(tz => { return tz.id === parseInt(val); });
            date.tz(timezoneObj.value, true);
            formApi.setValue('eventSchedule.eventStartUtc', date);
        }

        setShowTimezone(show);
        setTeamMatchEventScheduleStartDate(date);

        if (val) {
            setTimezone(parseInt(val));
        }
    };

    const handleDualMatchLocationChange = (optionValue) => {
        const selectedMatchLocationOption = teamMatchLocationOptions.find((option) => {
            return option.id === optionValue;
        });
        const clubIdOfLocation = selectedMatchLocationOption.clubId;
        const currentClubIsHome = clubIdOfLocation === defaultValues.club;
        const opponentClubIsHome = clubIdOfLocation === teamMatchSelectedOpponent.id;

        formApi.setValue(['eventLocations', 0, 'location'], selectedMatchLocationOption.value);
        handleTeamTennisClubSelect(teamMatchSelectedOpponent, currentClubIsHome, opponentClubIsHome);
    };

    const timeZones = getTimeZones(coreUITimeZones);

    return (
        <React.Fragment>
            <div className={`col-12 mt24 mb24`}>
                <div className="form-group form-group-xl">
                    <label data-testid="adultteamtennismatch.label-145">Opponent</label>
                    <TeamTennisClubSearch
                        placeholder={`Search for your opponent`}
                        handleTeamTennisClubSelect={(club) => handleTeamTennisClubSelect(club, true, false)}
                        clearAfterSelect={true}
                        closeAfterSelect={true}
                        displayClubNameAfterSelect={true}
                        showSearchClearIcon={true}
                        searchEndPoint="/clubs/teams"
                        entityId={defaultValues.club}
                        clubSearch={true}
                        handleClubClearClick={handleClubClearClick}
                    />
                </div>
                {showSelectIsStandings && (
                    <SelectIsConference
                        field="$isConference"
                        value={values.isConference}
                        formApi={formApi}
                    />
                )}
            </div>
            {values.dualMatchClubs && values.dualMatchClubs.length > 0 && (
                <React.Fragment>
                    <div className={`col-12 mb24`}>
                        <div className="div div16" />
                        <Input
                            label="Match Name"
                            id="name"
                            field="name"
                            type="hidden"
                        />
                    </div>
                    <div className={`col-12 mb24`}>
                        <NestedField field="eventSchedule" validateOnSubmit={false}>
                            <React.Fragment>
                                <DateTimePicker
                                    label="Date and Time"
                                    id="matchStart"
                                    field="eventStartUtc"
                                    placeholderText="Select date"
                                    selected={teamMatchEventScheduleStartDate}
                                    minDate={moment().add('years', -1)}
                                    maxDate={null}
                                    handleDateChange={(date) => {
                                        setTeamMatchEventScheduleStartDate(date);
                                    }}
                                    validate={required}
                                    data-testid="event-edit.match-start-date-picker"
                                />
                                <p className="text-small"><strong data-testid="adultteamtennismatch.p-196">Timezone: </strong>
                                    {(timezone ? timeZones.find(tz => tz.id === parseInt(timezone)).label : '')}
                                    <a
                                        href="#"
                                        className="underline"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            toggleTimezone(true);
                                        }}
                                    > change
                                    </a>
                                </p>
                                {showTimezone
                                    ? (
                                        <Select
                                            id="timezone"
                                            hiddenLabel={true}
                                            hiddenDefault={true}
                                            field="timeZone"
                                            options={timeZones}
                                            onChange={(val) => toggleTimezone(false, val)}
                                                data-testid="event-edit.timezone-select"
                                        />
                                    ) : null}
                            </React.Fragment>
                        </NestedField>
                    </div>
                    <div className={`col-12 mb24`}>
                        <Select
                            key={`match-location`}
                            id={`match-location`}
                            label="Location"
                            field={`clubLocationId`}
                            value={
                                teamMatchLocationOptions && teamMatchLocationOptions.length > 0 ? teamMatchLocationOptions[0] : ''
                            }
                            onChange={(optionValue) => handleDualMatchLocationChange(optionValue)}
                            hiddenDefault={true}
                            options={teamMatchLocationOptions}
                            validate={required}
                        />
                        <Input field={['eventLocations', 0, 'eventLocationType']} type="hidden" />
                    </div>

                    {lastEventIds && lastEventIds[values.eventType] && (
                        <div className={`col-12 mb24`}>
                            <CheckBox
                                id={`cloneLastEvent`}
                                customLabel={`Use my last event as a template`}
                                field={`cloneLastEvent`}
                                formGroupCssClass="mb16"
                            data-testid="event-edit.clone-last-event-input"
                            />
                        </div>
                    )}
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default AdultTeamTennisMatch;
